import type React from 'react';
import { Skeleton as TailGridsSkeleton } from '../tailgrids/core/skeleton.js';
import { cn } from '../../utils/cn.js';

export interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  /** `true` → circle/pill; a string → that radius class (e.g. `rounded-lg`); unset → small radius. */
  rounded?: boolean | string;
  className?: string;
}

/** A placeholder block shown while content loads (NextAdmin skeleton). Never hand-roll `animate-pulse`. */
export function Skeleton({ width, height, rounded, className }: SkeletonProps) {
  const radius = rounded === true ? 'rounded-full' : typeof rounded === 'string' ? rounded : 'rounded-sm';
  const style: React.CSSProperties = {};
  if (width !== undefined) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height !== undefined) style.height = typeof height === 'number' ? `${height}px` : height;

  return <TailGridsSkeleton className={cn(radius, className)} style={style} aria-hidden="true" />;
}

/** A list row placeholder: avatar, two text lines and a trailing chip. */
export function SkeletonRow({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center gap-3', className)} aria-hidden="true">
      <Skeleton width={32} height={32} rounded="rounded-lg" className="shrink-0" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <Skeleton height={12} width="55%" />
        <Skeleton height={10} width="38%" />
      </div>
      <Skeleton height={20} width={56} rounded className="shrink-0" />
    </div>
  );
}

/** `count` placeholder rows with an accessible "Loading" status for screen readers. */
export function SkeletonList({
  count = 5,
  label = 'Loading',
  className,
}: {
  count?: number;
  label?: string;
  className?: string;
}) {
  return (
    <div role="status" aria-label={label} className={cn('space-y-3', className)}>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonRow key={i} />
      ))}
    </div>
  );
}
